import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, Heart, RefreshCw, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { apiService } from '../services/api';
import { resolvePhotoUrl } from '../utils/photoUrl';
import { WhoLikedYou } from './WhoLikedYou';

const Avatar = ({ src, name, className }) => {
  const url = resolvePhotoUrl(src);
  return url ? <img src={url} alt={name} className={`${className} object-cover`} />
      : <div className={`${className} bg-gradient-to-br from-pink-200 to-red-200 flex items-center justify-center text-red-600 font-semibold`}>{(name || '?')[0]}</div>;
};

const formatTimeAgo = (timestamp) => {
  const diffInHours = Math.floor((Date.now() - new Date(timestamp)) / 36e5);
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  return `${Math.floor(diffInHours / 24)}d ago`;
};

const Matches = ({ onOpenChat, onUpgrade }) => {
  const [matches, setMatches] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  const loadMatches = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await apiService.swipes.getMatches();
      const list = res.data?.data;
      if (!res.data?.success || !Array.isArray(list)) throw new Error('Malformed matches response');
      setMatches(list);
      setStatus('ready');
    } catch (e) {
      console.error('Load matches error:', e);
      setStatus('error');
    }
  }, []);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  const refreshButton = (
    <Button variant="outline" size="sm" onClick={loadMatches} disabled={status === 'loading'} data-testid="matches-refresh-btn">
      <RefreshCw className={`w-4 h-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`} /> Refresh
    </Button>
  );

  const fresh = matches.filter((m) => !m.last_message);

  return (
    <div className="space-y-6" data-testid="matches-screen">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Your Matches</h1>
          <p className="text-gray-600">Start conversations with people who liked you back!</p>
        </div>
        {refreshButton}
      </div>

      <WhoLikedYou onUpgrade={onUpgrade} onMatched={loadMatches} />

      {status === 'loading' && (
        <div className="text-center py-12 text-gray-500" data-testid="matches-loading">Loading your matches…</div>
      )}

      {status === 'error' && (
        <div className="text-center py-12" data-testid="matches-error">
          <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <p className="text-red-600 font-medium mb-4">We couldn't load your matches. Please try again.</p>
          <Button onClick={loadMatches} data-testid="matches-error-retry-btn"><RefreshCw className="w-4 h-4 mr-2" /> Try again</Button>
        </div>
      )}

      {status === 'ready' && matches.length === 0 && (
        <div className="text-center py-12" data-testid="matches-empty">
          <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No matches yet.</h3>
          <p className="text-gray-500">A match appears here once you and someone else both like each other.</p>
        </div>
      )}

      {status === 'ready' && matches.length > 0 && (
        <>
          {fresh.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-500" /> New Matches
              </h2>
              <div className="flex space-x-3 overflow-x-auto pb-2">
                {fresh.map((match) => (
                  <button key={match.id} className="flex-shrink-0 text-center" onClick={() => onOpenChat(match.id)} data-testid={`new-match-${match.user_id}`}>
                    <Avatar src={match.image} name={match.name} className="w-20 h-20 rounded-full border-4 border-pink-400" />
                    <p className="text-xs mt-1 font-medium">{match.name}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MessageCircle className="w-5 h-5 mr-2 text-blue-500" /> Conversations
            </h2>
            <div className="space-y-3">
              {matches.map((match) => (
                <Card key={match.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => onOpenChat(match.id)} data-testid={`match-card-${match.user_id}`}>
                  <div className="flex items-center space-x-4">
                    <Avatar src={match.image} name={match.name} className="w-16 h-16 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 truncate" data-testid="match-name">{match.name}</h3>
                        <span className="text-xs text-gray-500">{formatTimeAgo(match.timestamp)}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate mt-1">{match.last_message || 'Say hello 👋'}</p>
                    </div>
                    {match.unread_count > 0 && <Badge className="bg-red-500 text-white">{match.unread_count}</Badge>}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Matches;
